import React, { Component } from 'react'
import { Chart } from 'chart.js/auto';
import classes from "./Graph.module.css";
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import GraphZoomControls from './GraphZoomControls.js';
import rangeArrowSource from '../../Images/Buttons/range-arrow.svg';

Chart.register(zoomPlugin);

const DAY_MS = 24 * 60 * 60 * 1000

// Snap the X span to whole days so the left-edge date tick lands on the daily
// grid instead of producing an extra label crammed against the next one (e.g.
// "31/05" stuck to "01/06") at the start of the axis.
const snapMinToDayStart = (ts) => { let d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); }
const snapMaxToDayEnd = (ts) => {
    let d = new Date(ts); d.setHours(0, 0, 0, 0);
    return d.getTime() === ts ? ts : d.getTime() + DAY_MS;
}

const getUsageBarThickness = (min, max, chartWidth) => {
    let dayCount = Math.max(1, Math.ceil((max - min) / DAY_MS))
    let availableWidthPerDay = chartWidth / dayCount
    let sixtyDayPeriods = Math.floor(dayCount / 60)
    let widthIncrease = 1 + (sixtyDayPeriods * 0.2)

    // Start from 72% of the available day column, then add 20% for each
    // complete 60-day period. Keep a small gap between adjacent days.
    return Math.max(
        1,
        Math.min(
            16,
            availableWidthPerDay * 0.95,
            availableWidthPerDay * 0.72 * widthIncrease
        )
    )
}

// Use detailed night ranges only when they fit. Longer periods use simple,
// evenly spaced dates so Chart.js cannot leave a few arbitrary labels.
const getXAxisDisplayConfig = (min, max) => {
    let dayCount = Math.max(1, Math.ceil((max - min) / DAY_MS))
    // Detailed night ranges ("D-1 → D") only for short spans (< 10 days), where
    // they fit. From 10 days up, fall back to simple evenly-spaced dates like the
    // AHI / breathing-rate / leakage graphs so labels don't overlap.
    // autoSkip: stepSize sets the base spacing, then Chart.js drops any remaining
    // labels that still don't fit so dates never overlap (like the AHI / rate
    // graphs). Kept on for every span — at short ranges nothing gets skipped
    // (the labels already fit), it only kicks in once they would collide (~26+ days).
    if (dayCount < 10) {
        return {nightRange: true, unit: 'day', stepSize: 1, maxTicks: Math.max(2, dayCount), autoSkip: true}
    }
    if (dayCount <= 30) {
        // Cap to ~5 labels so a 25-32 day span shows well-separated dates.
        return {nightRange: false, unit: 'day', stepSize: 6, maxTicks: 5, autoSkip: true}
    }
    if (dayCount <= 70) {
        return {nightRange: false, unit: 'day', stepSize: 7, maxTicks: 8, autoSkip: true}
    }
    if (dayCount <= 140) {
        return {nightRange: false, unit: 'day', stepSize: 14, maxTicks: 8, autoSkip: true}
    }
    if (dayCount <= 730) {
        let monthCount = Math.max(1, Math.ceil(dayCount / 30))
        return {nightRange: false, unit: 'month', stepSize: Math.max(1, Math.ceil(monthCount / 8)), maxTicks: 10, autoSkip: true}
    }
    let yearCount = Math.max(1, Math.ceil(dayCount / 365))
    return {nightRange: false, unit: 'year', stepSize: Math.max(1, Math.ceil(yearCount / 8)), maxTicks: 10, autoSkip: true}
}

const formatXAxisDate = (ts, unit) => {
    const pad = (n) => ('0' + n).slice(-2)
    let date = new Date(ts)
    if (unit === 'year') {
        return '' + date.getFullYear()
    }
    if (unit === 'month') {
        return pad(date.getMonth() + 1) + '/' + date.getFullYear()
    }
    return pad(date.getDate()) + '/' + pad(date.getMonth() + 1)
}

// The y axis is a 24h "noon to noon" clock: values run from 12 (noon, bottom)
// through 24 (midnight, middle) to 36 (noon next day, top). Convert an axis
// value back to its clock label.
const formatClockLabel = (value) => {
    let hour = (((Math.round(value) % 24) + 24) % 24)
    return (hour < 10 ? '0' + hour : '' + hour) + ':00'
}

// Format a timestamp as HH:MM (used in tooltips).
const formatClockTime = (ms) => {
    let d = new Date(ms)
    let hh = ('0' + d.getHours()).slice(-2)
    let mm = ('0' + d.getMinutes()).slice(-2)
    return hh + ':' + mm
}

// Format a duration (ms) compactly as "8h" or "8h30" (used for daily totals).
const formatDuration = (ms) => {
    let totalMin = Math.round(ms / 60000)
    let h = Math.floor(totalMin / 60)
    let m = totalMin % 60
    return m === 0 ? (h + 'h') : (h + 'h' + ('0' + m).slice(-2))
}

// Label a noon-to-noon column (ts = its end/wake day at midnight) as the night
// range D-1 to D. The arrow between these parts is drawn from an image.
const formatNightRangeParts = (ts) => {
    const pad = (n) => ('0' + n).slice(-2)
    let end = new Date(ts); end.setHours(0, 0, 0, 0)
    let start = new Date(end.getTime()); start.setDate(start.getDate() - 1)
    let sD = pad(start.getDate()), sM = pad(start.getMonth() + 1)
    let eD = pad(end.getDate()),  eM = pad(end.getMonth() + 1)
    return {
        start: sM === eM ? sD : sD + '/' + sM,
        end: eD + '/' + eM,
        startFull: sD + '/' + sM,
        endFull: eD + '/' + eM
    }
}

// Keep a text gap so Chart.js reserves enough room for the image label.
const formatNightRangePlaceholder = (ts) => {
    let range = formatNightRangeParts(ts)
    return range.start + '       ' + range.end
}

const createRangeArrowPlugin = (arrowImage) => ({
    id: 'usageRangeArrow',
    beforeEvent: (chart, args) => {
        if (args.event.type === 'mouseout') {
            chart.$usageHoverValue = undefined
        }
        else if (args.event.y !== undefined && chart.scales.y !== undefined) {
            chart.$usageHoverValue = chart.scales.y.getValueForPixel(args.event.y)
        }
    },
    afterDraw: (chart) => {
        if (!arrowImage.complete || arrowImage.naturalWidth === 0) {
            return
        }

        const xScale = chart.scales.x
        if (xScale !== undefined && getXAxisDisplayConfig(xScale.min, xScale.max).nightRange) {
            const labelItems = xScale.getLabelItems()
            labelItems.forEach((item, index) => {
                let range = formatNightRangeParts(xScale.ticks[index].value)
                let arrowWidth = 12.75
                let arrowHeight = 6
                let gap = 3

                chart.ctx.save()
                chart.ctx.translate(item.options.translation[0], item.options.translation[1])
                chart.ctx.rotate(item.options.rotation)
                chart.ctx.font = item.font.string
                chart.ctx.fillStyle = Chart.defaults.color
                chart.ctx.textAlign = 'left'
                chart.ctx.textBaseline = item.options.textBaseline

                let startWidth = chart.ctx.measureText(range.start).width
                let endWidth = chart.ctx.measureText(range.end).width
                let totalWidth = startWidth + gap + arrowWidth + gap + endWidth
                let startX = -totalWidth / 2
                let textY = item.textOffset

                chart.ctx.fillText(range.start, startX, textY)
                chart.ctx.drawImage(
                    arrowImage,
                    startX + startWidth + gap,
                    textY - arrowHeight / 2,
                    arrowWidth,
                    arrowHeight
                )
                chart.ctx.fillText(range.end, startX + startWidth + gap + arrowWidth + gap, textY)
                chart.ctx.restore()
            })
        }
    }
})

class GraphUsageSessions extends Component {
    constructor(props){
        super(props);
        this.myChart = "";
    }

    // Sum each day's session durations, keyed by the bar's x (day at midnight),
    // so the x axis can show the total usage for that day above its date.
    computeDailyTotals(curve) {
        let totals = {}
        if (curve !== undefined) {
            curve.forEach((bar) => {
                if (bar._start !== undefined && bar._end !== undefined) {
                    totals[bar.x] = (totals[bar.x] || 0) + (bar._end - bar._start)
                }
            })
        }
        return totals
    }

    componentDidMount() {
        var ctx = document.getElementById(this.props.id_graph);
        if (ctx === null) {
            ctx = this.props.new_window.document.getElementById(this.props.id_graph);
        }

        var local_max_x = 0;
        var local_min_x = 0;
        var curveDrawing = [];

        if(this.props.curve !== undefined){
            local_max_x  = snapMaxToDayEnd(this.props.max_x);
            local_min_x  = snapMinToDayStart(this.props.min_x);
            curveDrawing = this.props.curve;
        }
        const xAxisDisplay = getXAxisDisplayConfig(local_min_x, local_max_x)
        const barThickness = (context) => getUsageBarThickness(
            context.chart.options.scales.x.min,
            context.chart.options.scales.x.max,
            context.chart.chartArea ? context.chart.chartArea.width : context.chart.width
        )

        this.dailyTotals = this.computeDailyTotals(this.props.curve)

        const ImageConstructor = ctx.ownerDocument.defaultView.Image
        const rangeArrowImage = new ImageConstructor()
        rangeArrowImage.onload = () => {
            if (this.myChart !== undefined && this.myChart.draw !== undefined) {
                this.myChart.draw()
            }
        }
        rangeArrowImage.src = rangeArrowSource

        this.myChart = new Chart(ctx, {
            type: 'bar',
            plugins: [createRangeArrowPlugin(rangeArrowImage)],
            data: {
                datasets: [
                    {
                        label: this.props.curve_name,
                        data: curveDrawing,
                        backgroundColor: this.props.curve_color,
                        borderColor: this.props.curve_border_color || this.props.curve_color,
                        borderWidth: 1,
                        borderSkipped: false,
                        barThickness: barThickness,
                        maxBarThickness: 16,
                        // each session is plotted at its exact day: do not let chart.js
                        // dodge bars that share the same x (multiple sessions per day)
                        grouped: false,
                    }
                ],
            },
            options: {
                responsive: true,
                // taller than the default 2:1 so the 24h clock axis has room to breathe.
                // In the enlarged popup the container has a definite height, so fill it
                // exactly (otherwise the 1.3 ratio makes the canvas overflow and clips
                // the top/bottom of the y axis).
                maintainAspectRatio: this.props.fillHeight ? false : true,
                aspectRatio: 1.3,
                animation: false,
                plugins: {
                    title: {
                        color: 'black',
                        font : {weight: 'bold'},
                        display: this.props.title !== undefined,
                        text: this.props.title
                    },
                    zoom: {
                        pan: {
                            enabled: true,
                            mode: 'x',
                            threshold: 5,
                            modifierKey: 'shift',
                            onPanComplete: ({chart}) => {
                                let xAxisDisplay = getXAxisDisplayConfig(chart.scales.x.min, chart.scales.x.max)
                                chart.options.scales.x.time.unit = xAxisDisplay.unit
                                chart.options.scales.x.time.stepSize = xAxisDisplay.stepSize
                                chart.options.scales.x.ticks.autoSkip = xAxisDisplay.autoSkip
                                chart.options.scales.x.ticks.maxTicksLimit = xAxisDisplay.maxTicks
                                chart.update('none')
                            },
                        },
                        limits: {
                          x: {min: 'original', max: 'original', minRange: 3600000},
                          y: {min: 'original', max: 'original'},
                        },
                        zoom: {
                          wheel: {
                            enabled: true,
                            speed: 0.1,
                          },
                          pinch: {
                            enabled: true
                          },
                          drag: {
                            enabled: true,
                            backgroundColor: 'rgba(40, 150, 229, 0.15)',
                            borderColor: 'rgba(40, 150, 229, 0.8)',
                            borderWidth: 1,
                            threshold: 5,
                          },
                          mode: 'x',
                          onZoomComplete: ({chart}) => {
                            let xAxisDisplay = getXAxisDisplayConfig(chart.scales.x.min, chart.scales.x.max)
                            chart.options.scales.x.time.unit = xAxisDisplay.unit
                            chart.options.scales.x.time.stepSize = xAxisDisplay.stepSize
                            chart.options.scales.x.ticks.autoSkip = xAxisDisplay.autoSkip
                            chart.options.scales.x.ticks.maxTicksLimit = xAxisDisplay.maxTicks
                            chart.update('none')
                          },
                        }
                    },
                    tooltip: {
                        callbacks: {
                            // The lower half (12:00-00:00) belongs to the first
                            // date; the upper half (00:00-12:00) to the second.
                            title: (items) => {
                                if (!items || !items[0]) {
                                    return ''
                                }
                                let range = formatNightRangeParts(items[0].raw.x)
                                let hoverValue = items[0].chart.$usageHoverValue
                                return hoverValue !== undefined && hoverValue >= 24
                                    ? range.endFull
                                    : range.startFull
                            },
                            label: (tooltipItem) => {
                                let raw = tooltipItem.raw
                                if (raw === undefined || raw._start === undefined || raw._end === undefined) {
                                    return tooltipItem.dataset.label
                                }
                                // work in whole minutes so 60 min carries into the next
                                // hour (avoids "1h60" instead of "2h")
                                let durationMin = Math.round((raw._end - raw._start) / 60000)
                                let h = Math.floor(durationMin / 60)
                                let m = durationMin % 60
                                return tooltipItem.dataset.label + ': ' + formatClockTime(raw._start) + ' to ' + formatClockTime(raw._end) + ' (' + h + 'h' + ('0' + m).slice(-2) + ')'
                            },
                            // second line: total usage for that day
                            afterLabel: (tooltipItem) => {
                                let raw = tooltipItem.raw
                                if (raw === undefined || raw.x === undefined || this.dailyTotals === undefined) {
                                    return undefined
                                }
                                let total = this.dailyTotals[raw.x]
                                if (total === undefined || total <= 0) {
                                    return undefined
                                }
                                return 'Total journée : ' + formatDuration(total)
                            }
                        },
                        backgroundColor:'rgba(0,0,0,0.8)',  // black with transparency
                    },
                    legend: {
                        display: false
                    }
                },
                elements: {
                    point:{
                        hitRadius: 0
                    }
                },
                scales: {
                    x:
                    {
                        type: 'time',
                        min: local_min_x,
                        max: local_max_x,
                        distribution: 'series',
                        time: {
                            displayFormats: {
                                day: 'DD/MM',
                                month: 'DD/MM',
                            },
                            unit: xAxisDisplay.unit,
                            stepSize: xAxisDisplay.stepSize,
                            tooltipFormat: 'DD/MM'
                        },
                        ticks: {
                          autoSkip: xAxisDisplay.autoSkip,
                          autoSkipPadding: 24,
                          includeBounds: false,
                          maxTicksLimit: xAxisDisplay.maxTicks,
                          minRotation: 0,
                          maxRotation: 0,
                          major: {
                            enabled: true
                          },
                          color: function(context) {
                            let config = getXAxisDisplayConfig(context.scale.min, context.scale.max)
                            return config.nightRange ? 'rgba(0, 0, 0, 0)' : Chart.defaults.color
                          },
                          // Short ranges reserve room for the arrow image.
                          // Long ranges use simple dates at fixed intervals.
                          callback: function(value, index, ticks) {
                            let ts = (ticks && ticks[index] && ticks[index].value !== undefined) ? ticks[index].value : value
                            let config = getXAxisDisplayConfig(this.min, this.max)
                            // Drop the leftmost date for 26-31 day ranges (it crowds the axis start).
                            let dayCount = Math.max(1, Math.ceil((this.max - this.min) / DAY_MS))
                            if (!config.nightRange && index === 0 && dayCount >= 26 && dayCount <= 31) {
                                return ''
                            }
                            return config.nightRange
                                ? formatNightRangePlaceholder(ts)
                                : formatXAxisDate(ts, config.unit)
                          },
                          font: function(context) {
                            if (context.tick && context.tick.major) {
                              return {
                                weight: 'bold',
                              };
                            }
                          }
                        }
                    },
                    y:
                    {
                        type: 'linear',
                        // 12 = noon (bottom), 24 = midnight (middle), 36 = noon next day (top)
                        min: 12,
                        max: 36,
                        title: {
                            display: this.props.y_axis_title !== undefined,
                            text: this.props.y_axis_title,
                            color: this.props.curve_color
                        },
                        grid: {
                          drawOnChartArea: true,
                          color: (ctx) => (ctx.tick && ctx.tick.value === 24 ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)')
                        },
                        ticks: {
                          color: this.props.curve_color,
                          // one label every 4 hours, never skipped
                          stepSize: 4,
                          autoSkip: false,
                          callback: (value) => formatClockLabel(value)
                        }
                    }
                },
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (!this.myChart || !this.myChart.data) {
            return
        }

        const rangeChanged = prevProps.min_x !== this.props.min_x || prevProps.max_x !== this.props.max_x
        const curveChanged = prevProps.curve !== this.props.curve

        if (!rangeChanged && !curveChanged) {
            return
        }

        // Do not interrupt an in-progress drag selection with a React refresh.
        if (this.myChart.isZoomingOrPanning && this.myChart.isZoomingOrPanning()) {
            return
        }
        // Skip refresh while zoomed/panned so the periodic update does not snap
        // the view back to the full range. Resumes once the graph is reset.
        if (this.myChart.isZoomedOrPanned && this.myChart.isZoomedOrPanned()) {
            return
        }

        this.dailyTotals = this.computeDailyTotals(this.props.curve)
        this.myChart.data.datasets[0].data = this.props.curve

        if (rangeChanged) {
            this.myChart.options.scales.x.min = snapMinToDayStart(this.props.min_x)
            this.myChart.options.scales.x.max = snapMaxToDayEnd(this.props.max_x)
            let xAxisDisplay = getXAxisDisplayConfig(this.props.min_x, this.props.max_x)
            this.myChart.options.scales.x.time.unit = xAxisDisplay.unit
            this.myChart.options.scales.x.time.stepSize = xAxisDisplay.stepSize
            this.myChart.options.scales.x.ticks.autoSkip = xAxisDisplay.autoSkip
            this.myChart.options.scales.x.ticks.maxTicksLimit = xAxisDisplay.maxTicks
        }

        this.myChart.update('none')
    }

    componentWillUnmount() {
        if (this.myChart && this.myChart.destroy) {
            this.myChart.destroy()
        }
    }

    render() {
        return (
            <div className={classes.graphContainer} style={{display: "flex", flexDirection: "column", height: "100%", width: "100%"}}>
                <div style={{flex: "1 1 auto", minHeight: 0, width: "100%", display: "flex", justifyContent:'center', alignItems:'center'}}>
                    <canvas
                        style={{display: "flex", height: "100%", width: "100%", justifyContent:'center', alignItems:'center', touchAction: 'none', cursor: 'crosshair', userSelect: 'none'}}
                        id={this.props.id_graph}
                        ref={this.chartRef}
                    />
                </div>
                <GraphZoomControls
                    getChart={() => this.myChart}
                    afterChange={(chart) => {
                        let xAxisDisplay = getXAxisDisplayConfig(chart.scales.x.min, chart.scales.x.max)
                        chart.options.scales.x.time.unit = xAxisDisplay.unit
                        chart.options.scales.x.time.stepSize = xAxisDisplay.stepSize
                        chart.options.scales.x.ticks.autoSkip = xAxisDisplay.autoSkip
                        chart.options.scales.x.ticks.maxTicksLimit = xAxisDisplay.maxTicks
                        chart.update('none')
                    }}
                    onEnlarge={this.props.onEnlarge}
                />
            </div>
        )
    }
}
export default (GraphUsageSessions)
